import { on } from 'delegated-events'

import { trackEvent } from 'api/index'
import { getCsrfToken } from 'utils/csrf'
import { onDomReady } from 'utils/onDomReady'

import { loadCalendlyAssets, openCalendly } from './book-a-demo/calendly'
import {
  demoVariant,
  experimentVariant,
  trackExposure
} from './book-a-demo/experiment'
import {
  applyFieldState,
  buildFields,
  fieldFor,
  validateField
} from './book-a-demo/fields'

const EVENT_PREFIX = 'Marketing:BookDemo'

// Server-side error codes → user-facing messages and the field they attach to.
// Extend by adding new entries; no branching required in the submit handler.
const SERVER_FIELD_ERRORS = {
  email_public_domain: {
    field: 'email',
    message:
      'Please use your business email. Public email domains are not accepted.'
  }
}

// Server-side error codes shown in the form-level banner. Used for errors that
// do not belong to a single field (region blocks, throttling, etc.).
const SERVER_FORM_ERRORS = {
  region_not_supported:
    "We're sorry, Coupler.io demo isn't available in your region at this time."
}

const trackDemoEvent = (action, payload) => {
  trackEvent(`${EVENT_PREFIX}:${action}`, {
    demo_variant: demoVariant(),
    ...payload
  })
}

const analyticsPayload = values => ({
  role: values.role
})

const messageForServerError = code =>
  SERVER_FIELD_ERRORS[code]?.message || SERVER_FORM_ERRORS[code]

const submissionErrorReason = ({ code, status }) =>
  code || (status ? `http_${status}` : 'network_error')

const markStarted = root => {
  if (!root || root.dataset.demoStarted) return
  root.dataset.demoStarted = 'true'
  trackDemoEvent('Started')
}

const markFormSent = root => {
  const form = root.querySelector('form[data-demo-form-step="lead"]')

  form.querySelectorAll('input, button, .multiselect__toggle').forEach(el => {
    el.setAttribute('disabled', 'disabled')
  })
  finalSubmit(form).textContent = 'Request sent'
}

// Variant B splits the form in two steps: the business email with the privacy
// agreement first (the email is checked on the server before step 2), then the
// qualification details. Both live in one
// <form> so a single submit sends the whole lead; only the current step is
// visible and validated. Variant A's form has no steps.
const stepFor = (form, name) => form.querySelector(`[data-demo-step="${name}"]`)

// The button that sends the lead: the last step's one, or the only one.
const finalSubmit = form =>
  (stepFor(form, 'details') || form).querySelector('.demo-form__submit')

const currentStep = form => form.querySelector('[data-demo-step]:not(.hidden)')

const showStep = (form, name) => {
  form.querySelectorAll('[data-demo-step]').forEach(step => {
    step.classList.toggle('hidden', step.dataset.demoStep !== name)
  })
}

// Validates the given fields and focuses the first invalid one.
const validateAll = fields => {
  const results = fields.map(field => ({ field, ...validateField(field) }))
  const firstInvalid = results.find(r => r.invalid)
  if (firstInvalid) {
    trackDemoEvent('Validation failed')
    firstInvalid.field.control.focus()
  }
  return { results, valid: !firstInvalid }
}

const postJson = (url, body) =>
  fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': getCsrfToken()
    },
    body: JSON.stringify(body)
  })

// Resolves with the response body; rejects with the server's error code (if
// any) and the HTTP status, or neither for a network failure.
const postDemoJson = (url, body) =>
  postJson(url, body).then(async response => {
    const json = await response.json().catch(() => ({}))
    if (!response.ok) {
      const error = new Error(`HTTP ${response.status}`)
      error.code = json.error
      error.status = response.status
      throw error
    }
    return json
  })

const revalidateIfPreviouslyInvalid = e => {
  const form = e.currentTarget.form || e.currentTarget.closest('form')
  if (!form) return

  markStarted(form.closest('[data-demo-form]'))

  const field = fieldFor(buildFields(form), e.target)
  if (field?.control.hasAttribute('aria-invalid')) validateField(field)
}

on(
  'input',
  '[data-demo-form] input[name]:not([type="checkbox"])',
  revalidateIfPreviouslyInvalid
)
on(
  'change',
  '[data-demo-form] [data-demo-multiselect] .multiselect__checkbox',
  revalidateIfPreviouslyInvalid
)
on(
  'change',
  '[data-demo-form] [data-demo-privacy]',
  revalidateIfPreviouslyInvalid
)

// The call consent only applies to a typed phone number: it shows once there
// is one and hides, unticked, when the number is cleared.
const syncPhoneConsent = phone => {
  const consent = phone.form.querySelector('[data-demo-phone-consent]')
  const hasPhone = phone.value.trim() !== ''

  consent.classList.toggle('hidden', !hasPhone)
  if (!hasPhone) consent.querySelector('input').checked = false
}

on('input', '[data-demo-form] [data-demo-phone]', e =>
  syncPhoneConsent(e.currentTarget)
)

// The phone module (intl-tel-input), imported once the page has a phone field.
let phoneModule = null
const PHONE_WAIT_MS = 3000

// A typed number needs the phone module and its number utils to be sent in
// international format. Wait for them, but not for long: without them
// fields.js still adds the dial code itself.
const phoneReady = form => {
  if (!phoneModule || !form.querySelector('[data-demo-phone]')?.value.trim()) {
    return null
  }

  return Promise.race([
    phoneModule.then(({ loadPhoneUtils }) => loadPhoneUtils()),
    new Promise(resolve => setTimeout(resolve, PHONE_WAIT_MS))
  ]).catch(() => {})
}

onDomReady(() => {
  document
    .querySelectorAll('[data-demo-form] [data-demo-multiselect]')
    .forEach(box => {
      const toggle = box.querySelector('.multiselect__toggle')
      const error = box.querySelector('[data-demo-field-error]')
      if (toggle && error) toggle.setAttribute('aria-describedby', error.id)
    })

  if (document.querySelector('.book-a-demo [data-demo-form]')) {
    loadCalendlyAssets()
    trackExposure()
  }

  if (document.querySelector('[data-demo-form] [data-demo-phone]')) {
    phoneModule = import('./book-a-demo/phone')
    phoneModule.then(({ initPhoneInputs }) => initPhoneInputs())
  }

  // Firefox refills the phone on reload without an input event.
  document
    .querySelectorAll('[data-demo-form] [data-demo-phone]')
    .forEach(syncPhoneConsent)
})

const collectValues = results => {
  const values = {}
  results.forEach(({ field, value }) => {
    if (field.name === 'privacy') {
      values.privacy_agreed = true
    } else {
      values[field.name] = value
    }
  })
  return values
}

// A rejected field may sit on the first step (the email), so the form goes
// back to the step holding it before showing the error there.
const applyServerError = (form, fields, code) => {
  const handled = SERVER_FIELD_ERRORS[code]
  if (!handled) return false

  const target = fields.find(f => f.name === handled.field)
  if (!target) return false

  const step = target.control.closest('[data-demo-step]')
  if (step) showStep(form, step.dataset.demoStep)

  applyFieldState(target, handled.message)
  target.control.focus()
  return true
}

// Captures the banner's initial "generic error" text once, so custom messages
// can be applied and then restored on the next submit.
const showFormLevelError = (errorMessage, code) => {
  if (!errorMessage.dataset.defaultText) {
    errorMessage.dataset.defaultText = errorMessage.textContent
  }

  errorMessage.textContent =
    SERVER_FORM_ERRORS[code] || errorMessage.dataset.defaultText
  errorMessage.classList.remove('hidden')
}

// Shows a server rejection on its field, or in the form-level banner.
const showRejection = (form, fields, errorMessage, code) => {
  trackDemoEvent('Rejected', {
    error_message: messageForServerError(code) || code
  })
  const shownInline = applyServerError(form, fields, code)
  if (!shownInline) showFormLevelError(errorMessage, code)
}

// The server checks the email (business domain, supported region) before the
// visitor fills in step 2. If the check itself fails, the visitor moves on and
// the same check runs again on submit.
const completeEmailStep = async (form, errorMessage) => {
  const emailStep = stepFor(form, 'email')
  const fields = buildFields(emailStep)
  const { results, valid } = validateAll(fields)
  if (!valid) return

  const nextButton = emailStep.querySelector('.demo-form__submit')
  nextButton.setAttribute('disabled', 'disabled')
  const code = await postDemoJson(
    emailStep.dataset.demoCheckUrl,
    collectValues(results)
  ).then(
    () => null,
    error => (messageForServerError(error.code) ? error.code : null)
  )
  nextButton.removeAttribute('disabled')

  if (code) {
    showRejection(form, fields, errorMessage, code)
    return
  }

  trackDemoEvent('Email step completed')
  showStep(form, 'details')
  stepFor(form, 'details').querySelector('.multiselect__toggle')?.focus()
}

on('submit', '[data-demo-form] form[data-demo-form-step="lead"]', async e => {
  e.preventDefault()

  const form = e.currentTarget
  const root = form.closest('[data-demo-form]')
  const errorMessage = form.querySelector('[data-demo-form-error]')
  errorMessage.classList.add('hidden')

  markStarted(root)

  if (currentStep(form)?.dataset.demoStep === 'email') {
    await completeEmailStep(form, errorMessage)
    return
  }

  const submitButton = finalSubmit(form)

  // Disabled while waiting so a second click can't submit twice.
  submitButton.setAttribute('disabled', 'disabled')
  await phoneReady(form)
  submitButton.removeAttribute('disabled')

  const { results, valid } = validateAll(
    buildFields(stepFor(form, 'details') || form)
  )
  if (!valid) return

  // The email step was validated when the visitor left it.
  const emailStep = stepFor(form, 'email')
  const emailResults = emailStep
    ? buildFields(emailStep).map(field => ({ field, value: field.getValue() }))
    : []
  const fields = [...emailResults, ...results].map(r => r.field)
  const values = {
    ...collectValues([...emailResults, ...results]),
    phone_consent: form.querySelector('[data-demo-phone-consent] input')
      .checked,
    demo_variant: experimentVariant()
  }
  const payload = analyticsPayload(values)

  submitButton.setAttribute('disabled', 'disabled')

  postDemoJson(form.action, values)
    .then(() => {
      trackDemoEvent('Submitted', { ...payload, status: 'success' })
      trackDemoEvent('Qualified', payload)
      markFormSent(root)
      openCalendly(root.dataset.calendlyUrl, {
        name: [values.first_name, values.last_name].filter(Boolean).join(' '),
        email: values.email
      })
    })
    .catch(error => {
      const { code } = error
      trackDemoEvent('Submitted', {
        ...payload,
        status: 'error',
        error_reason: submissionErrorReason(error)
      })

      if (code) {
        showRejection(form, fields, errorMessage, code)
      } else {
        trackDemoEvent('Error', { status: error.status })
        showFormLevelError(errorMessage, code)
      }
      submitButton.removeAttribute('disabled')
    })
})
